import { useState, useCallback, Children } from 'react';
import type { CSSProperties } from 'react';
import { Check } from 'lucide-react';
import type { SDUIComponentProps } from '../../renderer/ComponentRenderer';
import { cn } from '@/lib/utils';
import { useArcheAction } from '../../hooks/useArcheAction';
import { useArcheNavigation } from '../../hooks/useArcheNavigation';
import { useArcheContext } from '../../hooks/useArcheContext';
import { Tabs as TabsPrimitive, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';

/**
 * Button component - clickable action element.
 */
export function Button({ id, props, style }: SDUIComponentProps) {
  const { submitForm, isExecuting } = useArcheAction();
  const { navigateTo } = useArcheNavigation();
  const { context } = useArcheContext();

  const {
    label,
    content, // Support 'content' as alias for 'label' (common in other frameworks)
    variant = 'primary',
    size = 'md',
    disabled = false,
    href,
    external = false,
    fullWidth = false,
    onClick,
    action,
    actionPayload,
    className,
  } = props as {
    label?: string;
    content?: string;
    variant?: 'primary' | 'secondary' | 'outline' | 'ghost' | 'danger';
    size?: 'sm' | 'md' | 'lg';
    disabled?: boolean;
    href?: string;
    external?: boolean;
    fullWidth?: boolean;
    onClick?: () => void;
    action?: 'submit' | 'navigate';
    actionPayload?: { page?: string } & Record<string, unknown>;
    className?: string;
  };

  // Use label if provided, otherwise fall back to content
  const buttonLabel = label || content || '';

  const variantClasses = {
    primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
    outline: 'border-2 border-primary text-primary hover:bg-primary/10',
    ghost: 'text-foreground hover:bg-accent',
    danger: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
  };

  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-base',
    lg: 'px-6 py-3 text-lg',
  };

  const baseClasses = cn(
    'inline-flex items-center justify-center font-medium rounded-md transition-colors',
    'focus:outline-none',
    'active:shadow-inner active:brightness-90',
    'disabled:opacity-50 disabled:cursor-not-allowed',
    variantClasses[variant],
    sizeClasses[size],
    fullWidth && 'w-full',
    className,
  );

  const handleClick = () => {
    if (onClick) {
      onClick();
    } else if (action === 'submit') {
      submitForm(context.input || {});
    } else if (action === 'navigate' && actionPayload?.page) {
      navigateTo(actionPayload.page);
    }
  };

  if (href) {
    const openInNewTab = external || href.startsWith('http');
    return (
      <a
        id={id}
        href={href}
        className={baseClasses}
        style={style as CSSProperties}
        target={openInNewTab ? '_blank' : undefined}
        rel={openInNewTab ? 'noopener noreferrer' : undefined}
      >
        {buttonLabel}
      </a>
    );
  }

  return (
    <button
      id={id}
      type="button"
      disabled={disabled || isExecuting}
      onClick={handleClick}
      className={baseClasses}
      style={style as CSSProperties}
    >
      {isExecuting && action === 'submit' ? 'Processing...' : buttonLabel}
    </button>
  );
}

/**
 * ButtonGroup component - group of buttons with consistent spacing.
 */
export function ButtonGroup({ id, props, style, children }: SDUIComponentProps) {
  const {
    direction = 'horizontal',
    spacing = 'md',
    className,
  } = props as {
    direction?: 'horizontal' | 'vertical';
    spacing?: 'sm' | 'md' | 'lg';
    className?: string;
  };

  const directionClasses = {
    horizontal: 'flex-row',
    vertical: 'flex-col',
  };

  const spacingClasses = {
    sm: direction === 'horizontal' ? 'space-x-2' : 'space-y-2',
    md: direction === 'horizontal' ? 'space-x-4' : 'space-y-4',
    lg: direction === 'horizontal' ? 'space-x-6' : 'space-y-6',
  };

  return (
    <div
      id={id}
      className={cn('flex', directionClasses[direction], spacingClasses[spacing], className)}
      style={style as CSSProperties}
    >
      {children}
    </div>
  );
}

/**
 * Link component - hyperlink to external or internal pages.
 */
export function Link({ id, props, style }: SDUIComponentProps) {
  const { text, href, target, className } = props as {
    text: string;
    href: string;
    target?: '_blank' | '_self';
    className?: string;
  };

  return (
    <a
      id={id}
      href={href}
      target={target}
      rel={target === '_blank' ? 'noopener noreferrer' : undefined}
      className={cn('text-primary hover:underline', className)}
      style={style as CSSProperties}
    >
      {text}
    </a>
  );
}

/**
 * Stepper component - multi-step progress indicator.
 */
export function Stepper({ id, props, style }: SDUIComponentProps) {
  const {
    steps = [],
    currentStep = 0,
    orientation = 'horizontal',
    className,
  } = props as {
    steps: Array<string | { label: string; description?: string }>;
    currentStep?: number;
    orientation?: 'horizontal' | 'vertical';
    className?: string;
  };

  // Normalize steps: accept string[] or object[] with label/description
  const normalizedSteps = steps.map((s) => (typeof s === 'string' ? { label: s } : s));

  if (orientation === 'vertical') {
    return (
      <div id={id} className={className} style={style as CSSProperties}>
        <nav aria-label="Progress">
          <ol className="flex flex-col">
            {normalizedSteps.map((step, index) => (
              <li key={index} className="relative flex gap-3">
                {/* Connector line + circle column */}
                <div className="flex flex-col items-center">
                  <div
                    className={cn(
                      'relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                      index < currentStep
                        ? 'bg-primary'
                        : index === currentStep
                          ? 'border-2 border-primary bg-background'
                          : 'border-2 border-muted-foreground/30 bg-background',
                    )}
                  >
                    {index < currentStep ? (
                      <Check className="h-5 w-5 text-primary-foreground" />
                    ) : (
                      <span
                        className={cn(
                          'text-sm font-medium',
                          index === currentStep ? 'text-primary' : 'text-muted-foreground',
                        )}
                      >
                        {index + 1}
                      </span>
                    )}
                  </div>
                  {index !== normalizedSteps.length - 1 && (
                    <div
                      className={cn(
                        'w-0.5 grow',
                        index < currentStep ? 'bg-primary' : 'bg-muted-foreground/30',
                      )}
                    />
                  )}
                </div>
                {/* Label + description column */}
                <div className={cn('pb-6', index === normalizedSteps.length - 1 && 'pb-0')}>
                  <span
                    className={cn(
                      'text-sm font-medium leading-8',
                      index <= currentStep ? 'text-foreground' : 'text-muted-foreground',
                    )}
                  >
                    {step.label}
                  </span>
                  {step.description && (
                    <p className="text-xs text-muted-foreground mt-0.5">{step.description}</p>
                  )}
                </div>
              </li>
            ))}
          </ol>
        </nav>
      </div>
    );
  }

  return (
    <div id={id} className={className} style={style as CSSProperties}>
      <nav aria-label="Progress">
        <ol className="flex w-full">
          {normalizedSteps.map((step, index) => {
            const isLast = index === normalizedSteps.length - 1;
            return (
              <li key={index} className={cn(isLast ? 'shrink-0' : 'flex-1 min-w-0')}>
                <div className="flex items-center">
                  <div
                    className={cn(
                      'relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full',
                      index < currentStep
                        ? 'bg-primary'
                        : index === currentStep
                          ? 'border-2 border-primary bg-background'
                          : 'border-2 border-muted-foreground/30 bg-background',
                    )}
                  >
                    {index < currentStep ? (
                      <Check className="h-5 w-5 text-primary-foreground" />
                    ) : (
                      <span
                        className={cn(
                          'text-sm font-medium',
                          index === currentStep ? 'text-primary' : 'text-muted-foreground',
                        )}
                      >
                        {index + 1}
                      </span>
                    )}
                  </div>
                  {!isLast && (
                    <div
                      className={cn(
                        'h-0.5 flex-1 mx-2',
                        index < currentStep ? 'bg-primary' : 'bg-muted-foreground/30',
                      )}
                    />
                  )}
                </div>
                <div className="mt-2 pr-2">
                  <span
                    className={cn(
                      'text-xs font-medium leading-tight block',
                      index <= currentStep ? 'text-foreground' : 'text-muted-foreground',
                    )}
                  >
                    {step.label}
                  </span>
                  {step.description && (
                    <p className="text-xs text-muted-foreground mt-0.5 leading-tight">
                      {step.description}
                    </p>
                  )}
                </div>
              </li>
            );
          })}
        </ol>
      </nav>
    </div>
  );
}

/**
 * ProgressBar component - horizontal progress indicator.
 */
export function ProgressBar({ id, props, style }: SDUIComponentProps) {
  const {
    value = 0,
    max = 100,
    showLabel = false,
    size = 'md',
    className,
  } = props as {
    value?: number;
    max?: number;
    showLabel?: boolean;
    size?: 'sm' | 'md' | 'lg';
    className?: string;
  };

  const percentage = Math.min(100, Math.max(0, (value / max) * 100));

  const sizeClasses = {
    sm: 'h-1',
    md: 'h-2',
    lg: 'h-4',
  };

  return (
    <div id={id} className={className} style={style as CSSProperties}>
      {showLabel && (
        <div className="flex justify-between text-sm text-muted-foreground mb-1">
          <span>Progress</span>
          <span>{Math.round(percentage)}%</span>
        </div>
      )}
      <div className={cn('w-full bg-muted rounded-full', sizeClasses[size])}>
        <div
          className={cn('bg-primary rounded-full transition-all duration-300', sizeClasses[size])}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Tabs component - tabbed content navigation.
 */
export function Tabs({ id, props, style, children }: SDUIComponentProps) {
  const {
    tabs = [],
    activeTab,
    variant = 'underline',
    size = 'md',
    fullWidth = false,
    onTabChange,
    className,
  } = props as {
    tabs: Array<{
      id: string;
      label: string;
      icon?: string;
      disabled?: boolean;
      badge?: string | number;
    }>;
    activeTab?: string;
    variant?: 'underline' | 'pills' | 'enclosed';
    size?: 'sm' | 'md' | 'lg';
    fullWidth?: boolean;
    onTabChange?: () => void;
    className?: string;
  };

  const [currentTab, setCurrentTab] = useState(activeTab || tabs[0]?.id);

  const handleTabChange = useCallback(
    (value: string) => {
      setCurrentTab(value);
      if (onTabChange) {
        onTabChange();
      }
    },
    [onTabChange],
  );

  const sizeClasses = {
    sm: 'text-sm h-8',
    md: 'text-sm h-10',
    lg: 'text-base h-12',
  };

  const variantListClasses = {
    underline: 'bg-transparent border-b border-border rounded-none p-0',
    pills: 'bg-muted rounded-md p-1',
    enclosed: 'bg-transparent border border-border border-b-0 rounded-t-md p-0',
  };

  const variantTriggerClasses = {
    underline: cn(
      'rounded-none border-b-2 border-transparent',
      'data-[state=active]:border-primary data-[state=active]:bg-transparent',
      'hover:border-muted-foreground/50',
    ),
    pills: cn('rounded-sm', 'data-[state=active]:bg-background data-[state=active]:shadow-sm'),
    enclosed: cn(
      'rounded-t-md border border-transparent border-b-0',
      'data-[state=active]:border-border data-[state=active]:bg-background',
      'data-[state=active]:mb-[-1px] data-[state=active]:pb-[1px]',
    ),
  };

  const childArray = Children.toArray(children);

  return (
    <TabsPrimitive
      id={id}
      value={currentTab}
      onValueChange={handleTabChange}
      className={cn('w-full', className)}
      style={style as CSSProperties}
    >
      <TabsList
        className={cn(variantListClasses[variant], fullWidth && 'w-full', sizeClasses[size])}
      >
        {tabs.map((tab) => (
          <TabsTrigger
            key={tab.id}
            value={tab.id}
            disabled={tab.disabled}
            className={cn(variantTriggerClasses[variant], fullWidth && 'flex-1', sizeClasses[size])}
          >
            {tab.label}
            {tab.badge !== undefined && (
              <span className="ml-2 px-1.5 py-0.5 text-xs rounded-full bg-primary/10 text-primary">
                {tab.badge}
              </span>
            )}
          </TabsTrigger>
        ))}
      </TabsList>
      {tabs.map((tab, index) => (
        <TabsContent key={tab.id} value={tab.id} className="mt-4">
          {childArray[index]}
        </TabsContent>
      ))}
    </TabsPrimitive>
  );
}
